import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import LeadScoring from './LeadScoring';
import CampaignFlowBuilder from './CampaignFlowBuilder';
import Ico from './ui/Ico';

// ─── Palette ──────────────────────────────────────────────────────────────────
const NAVY = '#0B1220';
const BLUE = '#2563EB';
const TEAL = '#00D4CB';

// ─── Channel config ───────────────────────────────────────────────────────────
const CHANNELS = {
  email:     { label: 'Email',     icon: <Ico e="📧" bare />, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  linkedin:  { label: 'LinkedIn',  icon: <Ico e="💼" bare />, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  whatsapp:  { label: 'WhatsApp',  icon: <Ico e="📱" bare />, color: 'bg-green-100 text-green-700 border-green-200' },
  sms:       { label: 'SMS',       icon: <Ico e="💬" bare />, color: 'bg-red-100 text-red-700 border-red-200' },
  wait:      { label: 'Aguardar',  icon: <Ico e="⏳" bare />, color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const STEP_TYPES = ['email', 'linkedin', 'whatsapp', 'sms', 'wait'];

const STATUS_CFG = {
  draft:     { label: 'Rascunho',  color: 'bg-gray-100 text-gray-600 border-gray-200' },
  active:    { label: 'Ativa',     color: 'bg-green-100 text-green-700 border-green-200' },
  paused:    { label: 'Pausada',   color: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Concluída', color: 'bg-blue-100 text-blue-700 border-blue-200' },
};

// ─── Small helpers ────────────────────────────────────────────────────────────
function Tag({ children, color = '' }) {
  return <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border font-medium ${color}`}>{children}</span>;
}
function Stat({ label, value, sub, color = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 text-center shadow-sm">
      <p className={`text-2xl font-bold ${color}`}>{value ?? 0}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}
function Modal({ open, onClose, title, children, maxW = 'max-w-5xl' }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/50 z-[9999] flex items-start justify-center p-4 overflow-y-auto"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} my-8`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}
// ─── Templates Tab ────────────────────────────────────────────────────────────
function TemplatesTab({ api }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const empty = { name: '', channel: 'email', subject: '', content: '', description: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    try { const r = await api.get('/unique-channel/templates'); setTemplates(r.data || []); }
    catch { toast.error('Erro ao carregar templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, channel: t.channel, subject: t.subject || '', content: t.content, description: t.description || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.name || !form.content) return toast.error('Nome e conteúdo são obrigatórios');
    setSaving(true);
    try {
      if (editing) await api.put(`/unique-channel/templates/${editing.id}`, form);
      else await api.post('/unique-channel/templates', form);
      toast.success(editing ? 'Template atualizado!' : 'Template criado!');
      setShowForm(false);
      fetch();
    } catch { toast.error('Erro ao salvar template'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover template?')) return;
    await api.delete(`/unique-channel/templates/${id}`);
    toast.success('Removido');
    fetch();
  };

  const visible = filter === 'all' ? templates : templates.filter(t => t.channel === filter);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {['all', 'email', 'linkedin', 'whatsapp'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
              {f === 'all' ? 'Todos' : CHANNELS[f]?.icon + ' ' + CHANNELS[f]?.label}
            </button>
          ))}
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">+ Novo Template</Button>
      </div>

      {loading ? <p className="text-center py-12 text-gray-400">Carregando…</p> : visible.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3"><Ico e="📝" bare /></div>
          <p className="font-medium text-gray-600">Nenhum template ainda</p>
          <p className="text-sm mt-1">Crie templates de email, LinkedIn e WhatsApp para usar nas campanhas</p>
          <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" onClick={openCreate}>Criar primeiro template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map(t => {
            const ch = CHANNELS[t.channel] || {};
            return (
              <Card key={t.id} className="border border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{ch.icon}</span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                        <Tag color={ch.color}>{ch.label}</Tag>
                      </div>
                    </div>
                  </div>
                  {t.subject && <p className="text-xs text-gray-500 mb-1"><Ico e="📌" bare />{t.subject}</p>}
                  <p className="text-xs text-gray-400 line-clamp-2 mb-3">{t.content?.replace(/<[^>]+>/g, '').slice(0, 100)}…</p>
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openEdit(t)}><Ico e="✏" />Editar</Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 text-xs" onClick={() => remove(t.id)}><Ico e="🗑" bare /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Editar Template' : 'Novo Template'} maxW="max-w-2xl">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome *</Label>
              <Input className="mt-1" placeholder="Ex: Boas-vindas Email" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Canal *</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}>
                <option value="email">Email</option>
                <option value="linkedin">LinkedIn</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
          </div>
          {form.channel === 'email' && (
            <div>
              <Label>Assunto do Email</Label>
              <Input className="mt-1" placeholder="Ex: Olá {{nome}}, temos uma proposta para você!" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
            </div>
          )}
          <div>
            <Label>Conteúdo *</Label>
            <div className="mt-1 mb-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-xs font-semibold text-blue-700 mb-2"><Ico e="📌" />Variáveis disponíveis — clique para inserir no conteúdo:</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: '{Nome}',     tip: 'Nome do lead' },
                  { label: '{empresa}',  tip: 'Empresa' },
                  { label: '{telefone}', tip: 'Telefone/WhatsApp' },
                  { label: '{email}',    tip: 'E-mail do lead' },
                  { label: '{cargo}',    tip: 'Cargo / função' },
                  { label: '{cidade}',   tip: 'Cidade' },
                  { label: '{linkedin}', tip: 'URL do LinkedIn' },
                  { label: '{website}',  tip: 'Website' },
                ].map(v => (
                  <button key={v.label} title={v.tip} type="button"
                    onClick={() => {
                      const ta = document.getElementById('tpl-content-area');
                      if (ta) {
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        const newVal = form.content.substring(0, start) + v.label + form.content.substring(end);
                        setForm(f => ({ ...f, content: newVal }));
                        setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + v.label.length; ta.focus(); }, 0);
                      } else {
                        setForm(f => ({ ...f, content: f.content + v.label }));
                      }
                    }}
                    className="px-2.5 py-1 bg-white border border-blue-200 rounded-lg text-xs font-mono text-blue-800 hover:bg-blue-100 hover:border-blue-400 transition-all shadow-sm">
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea id="tpl-content-area" className="mt-1 font-mono text-sm" rows={form.channel === 'email' ? 10 : 5}
              placeholder={form.channel === 'email' ? '<p>Olá {Nome},</p>\n<p>Gostaria de apresentar nossa solução para a {empresa}…</p>' : 'Olá {Nome}, tudo bem? Gostaria de conversar sobre oportunidades para a {empresa}…'}
              value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
          </div>
          <div>
            <Label>Descrição (opcional)</Label>
            <Input className="mt-1" placeholder="Nota interna sobre este template" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Salvando…' : 'Salvar Template'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Flow Builder (inside campaign form) ──────────────────────────────────────
function FlowBuilder({ steps, onChange, templates }) {
  const addStep = (type) => {
    const newStep = { id: Date.now().toString(), type, template_id: '', wait_days: 1, order: steps.length };
    onChange([...steps, newStep]);
  };
  const removeStep = (idx) => onChange(steps.filter((_, i) => i !== idx));
  const updateStep = (idx, field, value) => {
    const next = [...steps];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const move = (idx, dir) => {
    const next = [...steps];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((s, i) => ({ ...s, order: i })));
  };

  const templatesFor = (channel) => templates.filter(t => t.channel === channel);

  return (
    <div className="space-y-3">
      {steps.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl text-gray-400">
          <p className="text-sm">Adicione passos ao fluxo abaixo</p>
        </div>
      )}
      {steps.map((step, idx) => {
        const ch = CHANNELS[step.type] || {};
        return (
          <div key={step.id || idx} className="flex items-start gap-3">
            <div className="flex flex-col items-center pt-2 gap-0.5">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-20 text-xs">▲</button>
              <span className="text-xs text-gray-400 font-bold">{idx + 1}</span>
              <button onClick={() => move(idx, 1)} disabled={idx === steps.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-20 text-xs">▼</button>
            </div>
            <div className={`flex-1 border rounded-xl p-3 ${ch.color?.replace('text-', 'border-').split(' ')[0] || 'border-gray-200'} bg-white`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{ch.icon}</span>
                  <span className="font-semibold text-sm text-gray-800">{ch.label}</span>
                </div>
                <button onClick={() => removeStep(idx)} className="text-gray-300 hover:text-red-500 text-sm">✕</button>
              </div>
              {step.type === 'wait' ? (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Aguardar</label>
                  <input type="number" min="1" max="60" className="border border-gray-200 rounded-lg px-2 py-1 text-sm w-16 text-center"
                    value={step.wait_days || 1} onChange={e => updateStep(idx, 'wait_days', Number(e.target.value))} />
                  <label className="text-xs text-gray-500">dia(s)</label>
                </div>
              ) : (
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                  value={step.template_id || ''} onChange={e => updateStep(idx, 'template_id', e.target.value)}>
                  <option value="">— Selecionar template {ch.label} —</option>
                  {templatesFor(step.type).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
          </div>
        );
      })}

      {/* Add step buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        <p className="text-xs text-gray-400 w-full">Adicionar passo:</p>
        {STEP_TYPES.map(type => {
          const ch = CHANNELS[type];
          return (
            <button key={type} onClick={() => addStep(type)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-all hover:shadow-sm ${ch.color}`}>
              {ch.icon} {ch.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Campaign Form Modal (full-screen flow editor) ────────────────────────────
function CampaignFormModal({ open, onClose, onSaved, api, editing, synklyAccounts, templates, leadLists, integrationStatus = {} }) {
  const empty = { name: '', description: '', synkly_account_id: '', flow_data: null, lead_list_id: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        description: editing.description || '',
        synkly_account_id: editing.synkly_account_id || '',
        flow_data: editing.flow_data || null,
        lead_list_id: editing.lead_list_id || '',
      });
    } else {
      setForm(empty);
    }
  }, [editing, open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nome é obrigatório');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        synkly_account_id: form.synkly_account_id || null,
        flow_data: form.flow_data,
        lead_list_id: form.lead_list_id || null,
        steps: [],
      };
      if (editing) await api.put(`/unique-channel/campaigns/${editing.id}`, payload);
      else await api.post('/unique-channel/campaigns', payload);
      toast.success(editing ? 'Campanha atualizada!' : 'Campanha criada!');
      onSaved();
      onClose();
    } catch { toast.error('Erro ao salvar campanha'); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return ReactDOM.createPortal(
    <div

      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}
    >
      <div className="flex flex-col bg-white w-full h-full" style={{ maxWidth: 1100, maxHeight: '92vh', margin: 'auto', borderRadius: 16, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.35)' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow">
              <span className="text-white text-base"><Ico e="🔗" bare /></span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg leading-tight">
                {editing ? 'Editar Campanha' : 'Nova Campanha UniqueChannel'}
              </h3>
              <p className="text-xs text-gray-400">Monte o fluxo de automação arrastando os nós para o canvas</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none p-1 rounded-lg hover:bg-gray-100 transition-colors">✕</button>
        </div>

        {/* ── Top fields ─────────────────────────────────────────────────── */}
        <div className="flex items-end gap-4 px-6 py-3 border-b border-gray-100 flex-shrink-0 bg-gray-50">
          <div className="flex-1 min-w-0">
            <Label className="text-xs font-semibold text-gray-500">Nome da campanha *</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="Ex: Prospecção Diretores SP"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div style={{ width: 200 }}>
            <Label className="text-xs font-semibold text-gray-500">Conta Synkly</Label>
            <select
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 h-9 text-sm bg-white"
              value={form.synkly_account_id}
              onChange={e => setForm(f => ({ ...f, synkly_account_id: e.target.value }))}
            >
              <option value="">— Selecionar —</option>
              {synklyAccounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div style={{ width: 220 }}>
            <Label className="text-xs font-semibold text-gray-500">Lista de Leads *</Label>
            <select
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 h-9 text-sm bg-white"
              value={form.lead_list_id}
              onChange={e => setForm(f => ({ ...f, lead_list_id: e.target.value }))}
            >
              <option value="">— Selecionar lista —</option>
              {(leadLists || []).map(l => (
                <option key={l.id} value={l.id}>{l.name} ({l.lead_count || 0} leads)</option>
              ))}
            </select>
          </div>
          <div style={{ width: 220 }}>
            <Label className="text-xs font-semibold text-gray-500">Descrição</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="Objetivo desta campanha…"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
        </div>

        {/* ── Flow Canvas ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
          <CampaignFlowBuilder
            flowData={form.flow_data}
            templates={templates}
            integrationStatus={integrationStatus}
            onChange={({ nodes, edges }) => setForm(f => ({ ...f, flow_data: { nodes, edges } }))}
          />
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white px-6">
            {saving ? 'Salvando…' : (editing ? <><Ico e="💾" />Atualizar Campanha</> : <><Ico e="💾" />Criar Campanha</>)}
          </Button>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── Enroll Modal ─────────────────────────────────────────────────────────────
function EnrollModal({ open, onClose, api, campaignId, onEnrolled, sources }) {
  const [source, setSource] = useState('crm_leads');
  const [linkedinCampaignId, setLinkedinCampaignId] = useState('');
  const [manualText, setManualText] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  const enroll = async () => {
    setEnrolling(true);
    try {
      let payload = { source, lead_filters: {}, manual_leads: [], source_id: null };
      if (source === 'linkedin_campaign') payload.source_id = linkedinCampaignId;
      if (source === 'manual') {
        const lines = manualText.split('\n').filter(Boolean);
        payload.manual_leads = lines.map(l => {
          const [name, email, phone] = l.split(',').map(x => x.trim());
          return { name: name || '', email: email || '', phone: phone || '' };
        });
      }
      const res = await api.post(`/unique-channel/campaigns/${campaignId}/enroll`, payload);
      toast.success(`${res.data.enrolled} leads adicionados! (${res.data.skipped} ignorados)`);
      onEnrolled();
      onClose();
    } catch { toast.error('Erro ao adicionar leads'); }
    finally { setEnrolling(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Adicionar Leads à Campanha" maxW="max-w-lg">
      <div className="space-y-4">
        <div>
          <Label>Fonte dos leads</Label>
          <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={source} onChange={e => setSource(e.target.value)}>
            <option value="crm_leads">Leads do CRM ({sources.crm_leads_total} disponíveis)</option>
            <option value="linkedin_campaign">Aceitos em campanha LinkedIn</option>
            <option value="manual">Inserir manualmente</option>
          </select>
        </div>
        {source === 'linkedin_campaign' && (
          <div>
            <Label>Campanha LinkedIn</Label>
            <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={linkedinCampaignId} onChange={e => setLinkedinCampaignId(e.target.value)}>
              <option value="">— Selecionar —</option>
              {(sources.linkedin_campaigns || []).map(c => <option key={c.id} value={c.id}>{c.name} ({c.accepted_count} aceitos)</option>)}
            </select>
          </div>
        )}
        {source === 'manual' && (
          <div>
            <Label>Leads (um por linha: nome, email, telefone)</Label>
            <Textarea className="mt-1 font-mono text-sm" rows={6} placeholder={"João Silva, joao@empresa.com, +5511999990000\nMaria Costa, maria@empresa.com"} value={manualText} onChange={e => setManualText(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">Formato: nome, email, telefone (email e telefone opcionais)</p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enroll} disabled={enrolling} className="bg-blue-600 hover:bg-blue-700 text-white">{enrolling ? 'Adicionando…' : 'Adicionar Leads'}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Campaign Detail ──────────────────────────────────────────────────────────
function CampaignDetail({ campaign, api, onBack, templates, sources }) {
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lRes, aRes] = await Promise.all([
        api.get(`/unique-channel/campaigns/${campaign.id}/leads`),
        api.get(`/unique-channel/campaigns/${campaign.id}/analytics`),
      ]);
      setLeads(lRes.data || []);
      setAnalytics(aRes.data || {});
    } catch { toast.error('Erro ao carregar dados da campanha'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [campaign.id]);

  const runEngine = async () => {
    setRunning(true);
    try {
      const res = await api.post('/unique-channel/engine/run');
      toast.success(`Motor executado: ${res.data.processed} ações processadas`);
      fetchData();
    } catch { toast.error('Erro ao executar motor'); }
    finally { setRunning(false); }
  };

  const removeLead = async (leadId) => {
    await api.delete(`/unique-channel/campaigns/${campaign.id}/leads/${leadId}`);
    setLeads(l => l.filter(x => x.id !== leadId));
    toast.success('Lead removido');
  };

  const totalSteps = campaign.steps?.length || 0;
  const visibleLeads = filterStatus === 'all' ? leads : leads.filter(l => l.status === filterStatus);

  const stepLabel = (idx) => {
    const s = campaign.steps?.[idx];
    if (!s) return 'Concluído';
    const ch = CHANNELS[s.type];
    return `${ch?.icon} Passo ${idx + 1}: ${ch?.label}`;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 text-lg transition-colors">←</button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">{campaign.name}</h2>
            <Tag color={STATUS_CFG[campaign.status]?.color || ''}>{STATUS_CFG[campaign.status]?.label}</Tag>
          </div>
          {campaign.description && <p className="text-sm text-gray-500">{campaign.description}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><Ico e="🔄" />Atualizar</Button>
          <Button size="sm" onClick={runEngine} disabled={running} className="bg-teal-600 hover:bg-teal-700 text-white">
            {running ? <><Ico e="⏳" />Executando…</> : '▶ Executar Motor'}
          </Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowEnroll(true)}>+ Adicionar Leads</Button>
        </div>
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
        <Stat label="Total" value={analytics.total_enrolled} color="text-gray-800" />
        <Stat label="Ativos" value={analytics.active} color="text-blue-600" />
        <Stat label="Concluídos" value={analytics.completed} color="text-green-600" />
        <Stat label="Emails enviados" value={analytics.emails_sent} color="text-blue-600" />
        <Stat label="LinkedIn" value={analytics.linkedin_sent} color="text-indigo-600" />
        <Stat label="WhatsApp" value={analytics.whatsapp_sent} color="text-green-600" />
        <Stat label="Responderam" value={analytics.responded} color="text-teal-600" />
      </div>

      {/* Flow steps summary */}
      {campaign.steps && campaign.steps.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {campaign.steps.map((s, i) => {
            const ch = CHANNELS[s.type];
            const tmpl = templates.find(t => t.id === s.template_id);
            return (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium shrink-0 ${ch?.color}`}>
                  <span>{ch?.icon}</span>
                  <span>{s.type === 'wait' ? `${s.wait_days}d` : (tmpl?.name || ch?.label)}</span>
                </div>
                {i < campaign.steps.length - 1 && <span className="text-gray-300 shrink-0">→</span>}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Leads list */}
      <Card className="border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <p className="font-semibold text-gray-800">Leads na campanha ({leads.length})</p>
          <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
            {['all', 'active', 'completed'].map(f => (
              <button key={f} onClick={() => setFilterStatus(f)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${filterStatus === f ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500'}`}>
                {f === 'all' ? 'Todos' : f === 'active' ? 'Ativos' : 'Concluídos'}
              </button>
            ))}
          </div>
        </div>
        <CardContent className="p-0">
          {loading ? <p className="text-center py-10 text-gray-400">Carregando…</p> :
           visibleLeads.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-2"><Ico e="👥" bare /></p>
              <p className="font-medium text-gray-500">Nenhum lead ainda</p>
              <Button className="mt-3 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowEnroll(true)}>Adicionar Leads</Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {visibleLeads.map(lead => {
                const progress = totalSteps > 0 ? Math.round((lead.current_step / totalSteps) * 100) : 0;
                const lastActivity = lead.activities?.[lead.activities.length - 1];
                return (
                  <div key={lead.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {(lead.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 text-sm truncate">{lead.name || '(sem nome)'}</p>
                        <Tag color={lead.status === 'completed' ? 'bg-green-100 text-green-700 border-green-200' : lead.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200'}>
                          {lead.status === 'completed' ? <><Ico e="✅" />Concluído</> : lead.status === 'active' ? '▶ Ativo' : lead.status}
                        </Tag>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        {lead.email && <span className="text-xs text-gray-400"><Ico e="📧" bare />{lead.email}</span>}
                        {lead.phone && <span className="text-xs text-gray-400"><Ico e="📱" bare />{lead.phone}</span>}
                      </div>
                    </div>
                    <div className="w-32 shrink-0">
                      <div className="flex justify-between text-xs text-gray-400 mb-0.5">
                        <span>{stepLabel(lead.current_step)}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                    {lastActivity && <div className="text-xs text-gray-400 shrink-0 max-w-36 truncate">{lastActivity.note}</div>}
                    <button onClick={() => removeLead(lead.id)} className="text-gray-300 hover:text-red-500 shrink-0 text-sm">✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <EnrollModal open={showEnroll} onClose={() => setShowEnroll(false)} api={api} campaignId={campaign.id} onEnrolled={fetchData} sources={sources} />
    </div>
  );
}

// ─── Campaigns Dashboard Tab ───────────────────────────────────────────────────
function CampaignsDashboard({ api, templates, onOpenCampaign, integrationStatus = {} }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sources, setSources] = useState({ crm_leads_total: 0, linkedin_campaigns: [], synkly_accounts: [] });
  const [leadLists, setLeadLists] = useState([]);

  const fetch = async () => {
    setLoading(true);
    try {
      const [cRes, sRes, lRes] = await Promise.all([
        api.get('/unique-channel/campaigns'),
        api.get('/unique-channel/audience-sources'),
        api.get('/unique-channel/lead-lists'),
      ]);
      setCampaigns(cRes.data || []);
      setSources(sRes.data || {});
      setLeadLists(lRes.data || []);
    } catch { toast.error('Erro ao carregar campanhas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const toggleStatus = async (c) => {
    if (c.status === 'active') await api.post(`/unique-channel/campaigns/${c.id}/pause`);
    else await api.post(`/unique-channel/campaigns/${c.id}/activate`);
    toast.success(c.status === 'active' ? 'Campanha pausada' : 'Campanha ativada');
    fetch();
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Remover campanha "${c.name}"? Todos os leads serão removidos.`)) return;
    await api.delete(`/unique-channel/campaigns/${c.id}`);
    toast.success('Campanha removida');
    fetch();
  };

  const totalStats = campaigns.reduce((acc, c) => ({
    active: acc.active + (c.status === 'active' ? 1 : 0),
    enrolled: acc.enrolled + (c.enrolled_count || 0),
    completed: acc.completed + (c.completed_count || 0),
  }), { active: 0, enrolled: 0, completed: 0 });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Campanhas ativas" value={totalStats.active} color="text-blue-600" />
          <Stat label="Leads em sequências" value={totalStats.enrolled} color="text-gray-800" />
          <Stat label="Leads concluídos" value={totalStats.completed} color="text-green-600" />
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700 text-white ml-4 shrink-0">
          + Nova Campanha
        </Button>
      </div>

      {loading ? <p className="text-center py-12 text-gray-400">Carregando…</p> : campaigns.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4"><Ico e="🚀" bare /></div>
          <h3 className="text-lg font-bold text-gray-800 mb-1">Nenhuma campanha ainda</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">Crie sua primeira campanha UniqueChannel e conecte email, LinkedIn e WhatsApp em uma única sequência automatizada</p>
          <Button className="mt-5 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowForm(true)}>Criar primeira campanha</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const st = STATUS_CFG[c.status] || STATUS_CFG.draft;
            const channels = [...new Set((c.steps || []).filter(s => s.type !== 'wait').map(s => s.type))];
            return (
              <Card key={c.id} className="border border-gray-200 hover:border-blue-200 transition-all cursor-pointer group" onClick={() => onOpenCampaign({ ...c, sources })}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{c.name}</h3>
                        <Tag color={st.color}>{st.label}</Tag>
                        {channels.map(ch => <Tag key={ch} color={CHANNELS[ch]?.color}>{CHANNELS[ch]?.icon} {CHANNELS[ch]?.label}</Tag>)}
                      </div>
                      {c.description && <p className="text-xs text-gray-400 mb-2">{c.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span><Ico e="🔗" bare />{c.steps?.length || 0} passos</span>
                        <span><Ico e="👥" bare />{c.enrolled_count || 0} leads</span>
                        <span><Ico e="✅" bare />{c.completed_count || 0} concluídos</span>
                        <span>▶ {c.active_count || 0} ativos</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 ml-3 shrink-0" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(c)} className="text-xs">
                        {c.status === 'active' ? <><Ico e="⏸" />Pausar</> : '▶ Ativar'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-gray-700" onClick={() => { setEditing(c); setShowForm(true); }}><Ico e="✏" bare /></Button>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500" onClick={() => deleteCampaign(c)}><Ico e="🗑" bare /></Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-50">
                    <span className="text-xs text-blue-600 font-medium group-hover:underline">Abrir campanha →</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CampaignFormModal
        open={showForm}
        onClose={() => setShowForm(false)}
        onSaved={fetch}
        api={api}
        editing={editing}
        synklyAccounts={sources.synkly_accounts || []}
        templates={templates}
        leadLists={leadLists}
        integrationStatus={integrationStatus}
      />
    </div>
  );
}

// ─── Criação IA Tab ────────────────────────────────────────────────────────────

/** Canvas-based dominant color extractor — runs entirely in the browser */
function extractDominantColors(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX = 80;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const freq = {};
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 100) continue;                                      // skip transparent
        const r = data[i], g = data[i + 1], b = data[i + 2];
        if (r > 240 && g > 240 && b > 240) continue;               // skip near-white
        if (r < 20  && g < 20  && b < 20)  continue;               // skip near-black
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < 30 || lum > 230) continue;                        // skip very dark/light
        const key = `${Math.round(r/24)*24},${Math.round(g/24)*24},${Math.round(b/24)*24}`;
        freq[key] = (freq[key] || 0) + 1;
      }
      const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
      const toHex = ([r, g, b]) => '#' + [r, g, b].map(v => parseInt(v).toString(16).padStart(2, '0')).join('');
      const colors = sorted.slice(0, 5).map(([k]) => toHex(k.split(',')));
      resolve(colors.length >= 2 ? colors : ['#2563EB', '#00D4CB']);
    };
    img.onerror = () => resolve(['#2563EB', '#00D4CB']);
    img.src = dataUrl;
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function UploadZone({ label, hint, accept, multiple, files, onChange, preview }) {
  const ref = React.useRef();
  const handleDrop = e => { e.preventDefault(); const f = multiple ? [...e.dataTransfer.files] : [e.dataTransfer.files[0]]; onChange(f); };
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <div
        onDragOver={e => e.preventDefault()} onDrop={handleDrop}
        onClick={() => ref.current.click()}
        className="border-2 border-dashed border-gray-200 rounded-xl p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all text-center"
      >
        <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden"
          onChange={e => onChange(multiple ? [...e.target.files] : [e.target.files[0]])} />
        {preview && preview.length > 0 ? (
          <div className="flex flex-wrap gap-2 justify-center">
            {preview.map((src, i) => (
              <img key={i} src={src} alt="" className="h-16 w-auto rounded-lg object-contain border border-gray-200 bg-white" />
            ))}
            <span className="text-xs text-blue-500 self-center underline">Trocar arquivo</span>
          </div>
        ) : (
          <div className="py-2">
            <div className="text-2xl mb-1"><Ico e="📁" bare /></div>
            <p className="text-sm text-gray-500">{hint}</p>
            <p className="text-xs text-gray-400 mt-0.5">Clique ou arraste aqui</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CriacaoIATab({ api, onSavedToTemplates }) {
  const [step, setStep] = useState('home');
  const [form, setForm] = useState({
    briefing: '', brand_name: '', primary_color: '#2563EB',
    secondary_color: '#00D4CB', keywords: '', url_example: '',
    logo_base64: '', ref_images: [],
  });
  const [logoPreview, setLogoPreview]         = useState(null);
  const [refPreviews, setRefPreviews]         = useState([]);
  const [extracting, setExtracting]           = useState(false);
  const [colorsFromLogo, setColorsFromLogo]   = useState([]);
  const [outputType, setOutputType]           = useState('both');
  const [result, setResult]                   = useState(null);
  const [activePreview, setActivePreview]     = useState('landing_page');
  const [saving, setSaving]                   = useState('');
  // ── Image generation state (must be at top — React hooks rules) ──
  const [imgPrompt, setImgPrompt]             = useState('');
  const [imgStyle, setImgStyle]               = useState('modern');
  const [imgAspect, setImgAspect]             = useState('1:1');
  const [imgGenerating, setImgGenerating]     = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [imgError, setImgError]               = useState('');

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoUpload = async (files) => {
    if (!files[0]) return;
    setExtracting(true);
    try {
      const b64 = await fileToBase64(files[0]);
      setLogoPreview(b64);
      upd('logo_base64', b64);
      const colors = await extractDominantColors(b64);
      setColorsFromLogo(colors);
      upd('primary_color',   colors[0] || '#2563EB');
      upd('secondary_color', colors[1] || '#00D4CB');
      toast.success("Paleta extraída automaticamente do logotipo!");
    } catch { toast.error('Erro ao processar logotipo'); }
    finally { setExtracting(false); }
  };

  const handleRefImages = async (files) => {
    const b64s = await Promise.all(files.map(fileToBase64));
    setRefPreviews(b64s);
    upd('ref_images', b64s);
  };

  const generate = async () => {
    setStep('generating');
    try {
      const res = await api.post('/creative-ai/generate', { ...form, output_type: outputType });
      setResult(res.data);
      if (res.data.generated_images?.length) {
        setGeneratedImages(res.data.generated_images);
      }
      setActivePreview(outputType === 'email' ? 'email' : 'landing_page');
      setStep('result');
    } catch {
      toast.error('Erro ao gerar criativo. Verifique a chave Groq nas Config. de APIs.');
      setStep('type');
    }
  };

  const saveTemplate = async (type) => {
    const html = type === 'landing_page' ? result.landing_page : result.email;
    if (!html) return;
    setSaving(type);
    const label = type === 'landing_page' ? 'Landing Page' : 'Email';
    try {
      await api.post('/unique-channel/templates', {
        name: `${form.brand_name || 'Campanha'} — ${label} (IA)`,
        channel: type === 'email' ? 'email' : 'landing_page',
        subject: type === 'email' ? (result.email_subject || form.brand_name) : '',
        content: html,
        description: `Gerado por IA. Briefing: ${form.briefing.slice(0, 120)}`,
      });
      toast.success(`${label} salvo nos Templates!`);
      onSavedToTemplates && onSavedToTemplates();
    } catch { toast.error('Erro ao salvar template'); }
    finally { setSaving(''); }
  };

  const generateImages = async () => {
    const p = imgPrompt.trim() || `Marketing visual for ${form.brand_name}. ${form.briefing.slice(0, 120)}`;
    setImgGenerating(true);
    setImgError('');
    setGeneratedImages([]);
    try {
      const res = await api.post('/creative-ai/generate-image', {
        prompt: p,
        brand_name: form.brand_name,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        keywords: form.keywords,
        aspect_ratio: imgAspect,
        sample_count: 4,
        style: imgStyle,
      });
      setGeneratedImages(res.data.images || []);
      if (!res.data.images?.length) setImgError('Nenhuma imagem retornada. Tente outro prompt.');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erro ao gerar imagens';
      setImgError(msg);
    } finally { setImgGenerating(false); }
  };

  const downloadImage = (src, i) => {
    const a = document.createElement('a');
    a.href = src;
    a.download = `${form.brand_name || 'criativo'}-${imgAspect.replace(':', 'x')}-${i + 1}.png`;
    a.click();
  };

  // ── HOME ──
  if (step === 'home') return (
    <div className="flex flex-col items-center justify-center py-16 space-y-6 text-center">
      <div className="w-24 h-24 rounded-3xl flex items-center justify-center text-5xl shadow-2xl"
        style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}><Ico e="🎨" bare /></div>
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Motor Criativo IA</h2>
        <p className="text-gray-500 mt-2 max-w-lg">Descreva sua campanha, faça upload do logotipo e imagens de referência — a IA cria Landing Pages e Emails profissionais.</p>
      </div>
      <div className="flex gap-3 flex-wrap justify-center text-sm text-gray-500">
        {['Landing Pages responsivas','Templates de Email','Cores extraídas do logo','Imagens reais (Unsplash)','Salva nos Templates'].map(f => (
          <span key={f} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-4 py-1.5">
            <span className="text-green-500">✓</span> {f}
          </span>
        ))}
      </div>
      <button onClick={() => setStep('briefing')}
        className="px-8 py-4 rounded-2xl text-white font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
        style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}>
        <Ico e="✨" />Criar Campanha IA
      </button>
    </div>
  );

  // ── BRIEFING ──
  if (step === 'briefing') return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => setStep('home')} className="text-gray-400 hover:text-gray-600 text-sm">← Voltar</button>
        <h2 className="text-xl font-bold text-gray-900">1. Descreva sua campanha</h2>
        <span className="ml-auto text-xs text-gray-400">Passo 1 de 2</span>
      </div>

      {/* Briefing */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Briefing da campanha *</label>
        <textarea rows={4} value={form.briefing} onChange={e => upd('briefing', e.target.value)}
          placeholder="Descreva seus objetivos, público-alvo, diferenciais, o que quer comunicar, tom de voz desejado..."
          className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* Marca + Keywords */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nome da Marca / Empresa *</label>
          <input value={form.brand_name} onChange={e => upd('brand_name', e.target.value)}
            placeholder="Ex: NexoSynX CRM"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Palavras-chave</label>
          <input value={form.keywords} onChange={e => upd('keywords', e.target.value)}
            placeholder="Ex: tecnologia, inovação, B2B"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      {/* Logo Upload — cores automáticas */}
      <UploadZone
        label="Logotipo da marca (extrai cores automaticamente)"
        hint="PNG, SVG ou JPG — a IA lerá as cores do logo"
        accept="image/*"
        multiple={false}
        files={logoPreview ? [logoPreview] : []}
        preview={logoPreview ? [logoPreview] : null}
        onChange={handleLogoUpload}
      />

      {/* Paleta extraída */}
      {extracting && (
        <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
          Analisando logotipo e extraindo paleta de cores…
        </div>
      )}

      {colorsFromLogo.length > 0 && !extracting && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-green-800"><Ico e="✅" />Paleta extraída automaticamente do logotipo</p>
          <div className="flex items-center gap-2 flex-wrap">
            {colorsFromLogo.map((c, i) => (
              <button key={i} onClick={() => {
                if (i === 0) upd('primary_color', c);
                else if (i === 1) upd('secondary_color', c);
              }}
                title={i === 0 ? 'Principal' : i === 1 ? 'Secundária' : 'Clique para usar'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all hover:scale-105"
                style={{ background: c + '22', borderColor: c, color: c }}>
                <span className="w-3 h-3 rounded-full" style={{ background: c }} />
                {c.toUpperCase()}
                {i === 0 && <span className="text-gray-500 font-normal">(principal)</span>}
                {i === 1 && <span className="text-gray-500 font-normal">(secundária)</span>}
              </button>
            ))}
          </div>
          <p className="text-xs text-green-600">Clique em qualquer cor para usá-la como principal ou secundária</p>
        </div>
      )}

      {/* Cores com override manual */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Cor Principal
            {colorsFromLogo.length > 0 && <span className="ml-1 text-xs text-green-600 font-normal">• extraída do logo</span>}
          </label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={e => upd('primary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
            <input value={form.primary_color} onChange={e => upd('primary_color', e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Cor Secundária
            {colorsFromLogo.length > 1 && <span className="ml-1 text-xs text-green-600 font-normal">• extraída do logo</span>}
          </label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.secondary_color} onChange={e => upd('secondary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
            <input value={form.secondary_color} onChange={e => upd('secondary_color', e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
      </div>

      {/* Imagens de referência */}
      <UploadZone
        label="Imagens de referência (opcional — até 5 fotos)"
        hint="Fotos, prints ou imagens que a IA usará como inspiração visual"
        accept="image/*"
        multiple={true}
        files={refPreviews}
        preview={refPreviews.length > 0 ? refPreviews : null}
        onChange={handleRefImages}
      />

      {/* URL de referência */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">URL de site de referência (estilo visual)</label>
        <input value={form.url_example} onChange={e => upd('url_example', e.target.value)}
          placeholder="https://site-que-gosto-do-estilo.com"
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <button
        disabled={extracting}
        onClick={() => {
          if (!form.briefing.trim() || !form.brand_name.trim()) { toast.error('Preencha o briefing e o nome da marca'); return; }
          setStep('type');
        }}
        className="w-full py-3.5 rounded-xl text-white font-bold text-base shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}>
        Próximo → Escolher o que criar
      </button>
    </div>
  );

  // ── TYPE SELECTION ──
  if (step === 'type') return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => setStep('briefing')} className="text-gray-400 hover:text-gray-600">← Voltar</button>
        <h2 className="text-xl font-bold text-gray-900">2. O que criar?</h2>
        <span className="ml-auto text-xs text-gray-400">Passo 2 de 2</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { id: 'landing_page', icon: <Ico e="🖥" bare />, title: 'Landing Page', desc: 'Página web completa e responsiva para conversão de visitantes' },
          { id: 'email',        icon: <Ico e="📧" bare />, title: 'Template Email', desc: 'Template de email mkt compatível com Gmail, Outlook e Apple Mail' },
          { id: 'both',         icon: <Ico e="🎯" bare />, title: 'Ambos', desc: 'Cria Landing Page + Template de Email em uma só geração (recomendado)', recommended: true },
        ].map(opt => (
          <button key={opt.id} onClick={() => setOutputType(opt.id)}
            className={`relative p-5 rounded-2xl border-2 text-left transition-all hover:shadow-lg ${outputType === opt.id ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
            {opt.recommended && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-3 py-0.5 rounded-full">
                <Ico e="⭐" />Recomendado
              </span>
            )}
            <div className="text-3xl mb-3">{opt.icon}</div>
            <p className="font-bold text-gray-900 mb-1">{opt.title}</p>
            <p className="text-xs text-gray-500">{opt.desc}</p>
            {outputType === opt.id && (
              <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 text-sm text-blue-700 border border-blue-100">
        <Ico e="🤖" bare /><strong>Motor:</strong> Groq LLaMA 3.3 70B · <strong>Imagens:</strong> Unsplash (reais, gratuitas) · <strong>Tempo estimado:</strong> 15–40 segundos
      </div>

      <button onClick={generate}
        className="w-full py-4 rounded-xl text-white font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.01] transition-all"
        style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}>
        <Ico e="🚀" />Gerar Criativo com IA
      </button>
    </div>
  );

  // ── GENERATING ──
  if (step === 'generating') return (
    <div className="flex flex-col items-center justify-center py-20 space-y-6 text-center">
      <div className="relative w-24 h-24">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
        <div className="absolute inset-0 rounded-full border-4 border-t-blue-600 animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-3xl"><Ico e="🎨" bare /></div>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Criando seu criativo...</h2>
        <p className="text-gray-500 mt-2">A IA está escrevendo HTML, CSS e conteúdo persuasivo.<br/>Isso leva entre 15 e 40 segundos.</p>
      </div>
      <div className="flex gap-2 mt-2">
        {['Analisando briefing', 'Gerando HTML', 'Aplicando design', 'Finalizando'].map((s, i) => (
          <span key={s} className="text-xs text-gray-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}></span>
            {s}
          </span>
        ))}
      </div>
    </div>
  );

  // ── RESULT ──
  const hasBoth = result?.landing_page && result?.email;
  const hasLP   = !!result?.landing_page;
  const hasEM   = !!result?.email;

  const RESULT_TABS = [
    ...(hasLP ? [{ id: 'landing_page', label: <><Ico e="🖥" />Landing Page</> }] : []),
    ...(hasEM ? [{ id: 'email',        label: <><Ico e="📧" />Email Template</> }] : []),
    { id: 'imagens', label: <><Ico e="🖼" />Criativos Visuais</> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => { setStep('home'); setResult(null); setGeneratedImages([]); }}
            className="text-gray-400 hover:text-gray-600 text-sm">← Nova campanha</button>
          <h2 className="text-xl font-bold text-gray-900"><Ico e="✅" />Criativo gerado!</h2>
        </div>
        <button onClick={() => setStep('type')}
          className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
          <Ico e="🔄" />Regerar
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {RESULT_TABS.map(t => (
          <button key={t.id} onClick={() => setActivePreview(t.id)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activePreview === t.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Landing Page Preview */}
      {activePreview === 'landing_page' && hasLP && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Preview da Landing Page — <span className="text-blue-600 font-medium">{form.brand_name}</span></p>
            <button onClick={() => saveTemplate('landing_page')} disabled={saving === 'landing_page'}
              className="px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60 hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}>
              {saving === 'landing_page' ? 'Salvando…' : <><Ico e="💾" />Salvar nos Templates</>}
            </button>
          </div>
          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xl">
            <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b border-gray-200">
              <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"/><div className="w-3 h-3 rounded-full bg-yellow-400"/><div className="w-3 h-3 rounded-full bg-green-400"/></div>
              <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-400 text-center">{form.brand_name || 'landing-page'}.html</div>
            </div>
            <iframe srcDoc={result.landing_page} title="Landing Page Preview"
              className="w-full border-0" style={{ height: '600px' }} sandbox="allow-scripts allow-same-origin" />
          </div>
        </div>
      )}

      {/* Email Preview */}
      {activePreview === 'email' && hasEM && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Preview do Email Template</p>
              {result.email_subject && <p className="text-xs text-blue-600 mt-0.5"><Ico e="📌" />Assunto: {result.email_subject}</p>}
            </div>
            <button onClick={() => saveTemplate('email')} disabled={saving === 'email'}
              className="px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60 hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}>
              {saving === 'email' ? 'Salvando…' : <><Ico e="💾" />Salvar nos Templates</>}
            </button>
          </div>
          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xl">
            <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b border-gray-200">
              <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"/><div className="w-3 h-3 rounded-full bg-yellow-400"/><div className="w-3 h-3 rounded-full bg-green-400"/></div>
              <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-400 text-center">
                {result.email_subject || 'email-template.html'}
              </div>
            </div>
            <div className="bg-gray-50 flex justify-center p-6">
              <iframe srcDoc={result.email} title="Email Preview"
                className="border-0 shadow-lg rounded-lg" style={{ width: '600px', height: '700px' }}
                sandbox="allow-scripts allow-same-origin" />
            </div>
          </div>
        </div>
      )}

      {/* ── Criativos Visuais — imagens geradas junto com a campanha ── */}
      {activePreview === 'imagens' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-2xl p-5 flex items-center gap-4"
            style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)' }}>
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-2xl shrink-0"><Ico e="🖼" bare /></div>
            <div>
              <h3 className="text-white font-bold text-lg">Criativos Visuais com FLUX</h3>
              <p className="text-white/60 text-sm">
                {generatedImages.length > 0
                  ? 'Imagens geradas junto com a campanha e já incorporadas na Landing Page e no Email'
                  : 'Gere variações adicionais de criativos para a sua campanha'}
              </p>
            </div>
            <div className="ml-auto text-right hidden sm:block">
              <p className="text-white/40 text-xs">Powered by</p>
              <p className="text-white font-semibold text-sm">FLUX · Pollinations</p>
            </div>
          </div>

          {/* Auto-generated images (from the campaign generation) */}
          {generatedImages.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {generatedImages.length} criativos gerados automaticamente
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Estas imagens já estão incorporadas na sua Landing Page e Email Template
                  </p>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full font-semibold flex items-center gap-1">
                  <Ico e="✅" />Integradas na campanha
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {generatedImages.map((src, i) => (
                  <div key={i} className="group relative rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02]"
                    onClick={() => downloadImage(src, i)}>
                    <img src={src} alt={`Criativo ${i + 1}`} className="w-full h-auto object-cover" style={{ aspectRatio: '16/9' }} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all bg-white rounded-full px-4 py-2 text-sm font-semibold text-gray-800 shadow-lg">
                        <Ico e="⬇" />Baixar PNG
                      </div>
                    </div>
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      {i === 0 ? 'Hero / Banner' : 'Feature'}
                    </div>
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      {i + 1}/{generatedImages.length}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {generatedImages.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"/>
              <span className="text-xs text-gray-400 font-medium">Gerar variações adicionais</span>
              <div className="flex-1 h-px bg-gray-200"/>
            </div>
          )}

          {/* Manual generation controls */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prompt personalizado (opcional)</label>
              <textarea rows={2} value={imgPrompt}
                onChange={e => setImgPrompt(e.target.value)}
                placeholder={`Ex: Escritório moderno, equipe diversificada, tons de ${form.primary_color}. Deixe vazio para usar o briefing.`}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Formato</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: '1:1',  label: '1:1',  sub: 'Post / Feed' },
                    { id: '16:9', label: '16:9', sub: 'Banner' },
                    { id: '9:16', label: '9:16', sub: 'Stories' },
                    { id: '4:5',  label: '4:5',  sub: 'Retrato' },
                  ].map(a => (
                    <button key={a.id} onClick={() => setImgAspect(a.id)}
                      className={`p-2.5 rounded-xl border-2 text-center transition-all ${imgAspect === a.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                      <div className="text-xs font-bold text-gray-800">{a.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{a.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Estilo visual</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'modern',  label: <><Ico e="🏢" /></>, sub: 'Moderno' },
                    { id: 'elegant', label: <><Ico e="✨" /></>, sub: 'Elegante' },
                    { id: 'bold',    label: <><Ico e="⚡" /></>, sub: 'Impactante' },
                    { id: 'minimal', label: '◻️', sub: 'Minimal' },
                  ].map(s => (
                    <button key={s.id} onClick={() => setImgStyle(s.id)}
                      className={`p-2.5 rounded-xl border-2 text-center transition-all ${imgStyle === s.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                      <div className="text-lg">{s.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{s.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={generateImages} disabled={imgGenerating}
              className="w-full py-3 rounded-xl text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460, #533483)' }}>
              {imgGenerating ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Gerando com FLUX… (15-30s)
                </>
              ) : <><Ico e="🔄" />Gerar {generatedImages.length > 0 ? 'Mais Variações' : '4 Criativos'} com FLUX</>}
            </button>
          </div>

          {imgError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <strong>Erro:</strong> {imgError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Lead Lists Tab ───────────────────────────────────────────────────────────
function LeadListsTab({ api }) {
  const [lists, setLists]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName]     = useState('');
  const [newDesc, setNewDesc]     = useState('');
  const [creating, setCreating]   = useState(false);
  const [viewList, setViewList]   = useState(null);
  const [viewLeads, setViewLeads] = useState([]);
  const [viewTotal, setViewTotal] = useState(0);
  const [viewPage, setViewPage]   = useState(1);
  const [importing, setImporting] = useState(null); // list_id being imported
  const [csvFile, setCsvFile]     = useState(null);
  const [csvListId, setCsvListId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const loadLists = async () => {
    setLoading(true);
    try { const r = await api.get('/unique-channel/lead-lists'); setLists(r.data || []); }
    catch { toast.error('Erro ao carregar listas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadLists(); }, []);

  const createList = async () => {
    if (!newName.trim()) return toast.error('Nome é obrigatório');
    setCreating(true);
    try {
      await api.post('/unique-channel/lead-lists', { name: newName, description: newDesc });
      toast.success('Lista criada!');
      setShowCreate(false); setNewName(''); setNewDesc('');
      loadLists();
    } catch { toast.error('Erro ao criar lista'); }
    finally { setCreating(false); }
  };

  const deleteList = async (id, name) => {
    if (!window.confirm(`Remover a lista "${name}" e todos os seus leads?`)) return;
    await api.delete(`/unique-channel/lead-lists/${id}`);
    toast.success('Lista removida');
    loadLists();
  };

  const openViewLeads = async (lst) => {
    setViewList(lst); setViewPage(1);
    try {
      const r = await api.get(`/unique-channel/lead-lists/${lst.id}/leads?page=1&limit=50`);
      setViewLeads(r.data.leads || []); setViewTotal(r.data.total || 0);
    } catch { toast.error('Erro ao carregar leads'); }
  };

  const importFromCRM = async (listId) => {
    setImporting(listId);
    try {
      const r = await api.post(`/unique-channel/lead-lists/${listId}/import-from-crm`, {});
      toast.success(`${r.data.created} leads importados do CRM!`);
      loadLists();
    } catch { toast.error('Erro ao importar do CRM'); }
    finally { setImporting(null); }
  };

  const importFromLinkedIn = async (listId) => {
    setImporting(listId);
    try {
      const r = await api.post(`/unique-channel/lead-lists/${listId}/import-from-linkedin`);
      toast.success(`${r.data.created} leads importados do LinkedIn!`);
      loadLists();
    } catch { toast.error('Erro ao importar do LinkedIn'); }
    finally { setImporting(null); }
  };

  const handleCSVUpload = async () => {
    if (!csvFile || !csvListId) return;
    setUploading(true); setUploadResult(null);
    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('Arquivo vazio ou sem dados'); setUploading(false); return; }

      // Parse CSV
      const parseCSVLine = (line) => {
        const result = []; let current = ''; let inQuotes = false;
        for (let c of line) {
          if (c === '"') { inQuotes = !inQuotes; }
          else if (c === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
          else { current += c; }
        }
        result.push(current.trim());
        return result;
      };

      const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map(line => {
        const vals = parseCSVLine(line).map(v => v.replace(/^"|"$/g, ''));
        const obj = {};
        headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
        return obj;
      }).filter(r => Object.values(r).some(v => v));

      const r = await api.post(`/unique-channel/lead-lists/${csvListId}/leads/bulk`, { leads: rows });
      setUploadResult(r.data);
      toast.success(`${r.data.created} leads adicionados!`);
      loadLists();
    } catch { toast.error('Erro ao importar CSV'); }
    finally { setUploading(false); }
  };

  const SOURCE_BADGE = {
    manual:      { label: 'Manual',    bg: '#f1f5f9', color: '#475569', icon: <Ico e="✏" bare /> },
    crm:         { label: 'CRM',       bg: '#dbeafe', color: '#1d4ed8', icon: <Ico e="🏢" bare /> },
    linkedin:    { label: 'LinkedIn',  bg: '#e0e7ff', color: '#4338ca', icon: <Ico e="💼" bare /> },
    bulk_upload: { label: 'Upload',    bg: '#d1fae5', color: '#065f46', icon: <Ico e="📤" bare /> },
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900"><Ico e="📋" />Listas de Leads</h2>
          <p className="text-xs text-gray-400 mt-0.5">Organize seus contatos em listas e vincule-as às campanhas</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
          + Nova Lista
        </Button>
      </div>

      {/* CSV upload section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl"><Ico e="📤" bare /></span>
          <div className="flex-1">
            <p className="font-semibold text-green-800 text-sm">Importar leads via planilha CSV</p>
            <p className="text-xs text-green-600 mt-0.5 mb-3">Colunas reconhecidas automaticamente: <code className="bg-white px-1 rounded">Nome</code>, <code className="bg-white px-1 rounded">Email</code>, <code className="bg-white px-1 rounded">Telefone</code>, <code className="bg-white px-1 rounded">Empresa</code>, <code className="bg-white px-1 rounded">Cargo</code>, <code className="bg-white px-1 rounded">Cidade</code>, <code className="bg-white px-1 rounded">LinkedIn</code></p>
            <div className="flex items-center gap-3 flex-wrap">
              <select className="border border-green-300 rounded-lg px-3 py-2 text-sm bg-white"
                value={csvListId || ''} onChange={e => setCsvListId(e.target.value)}>
                <option value="">Selecionar lista de destino…</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <label className="flex items-center gap-2 px-4 py-2 bg-white border border-green-300 rounded-lg text-sm text-green-700 cursor-pointer hover:bg-green-50 transition-all">
                <Ico e="📁" bare />{csvFile ? csvFile.name : 'Escolher arquivo .csv'}
                <input type="file" accept=".csv,.txt" className="hidden" onChange={e => setCsvFile(e.target.files[0])} />
              </label>
              <button onClick={handleCSVUpload} disabled={!csvFile || !csvListId || uploading}
                className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all">
                {uploading ? <><Ico e="⏳" />Importando…</> : <><Ico e="⬆" />Importar</>}
              </button>
            </div>
            {uploadResult && (
              <p className="text-xs text-green-700 mt-2 font-medium">
                <Ico e="✅" bare />{uploadResult.created} leads adicionados · {uploadResult.errors} ignorados (duplicatas ou sem dados)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Lists grid */}
      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Carregando listas…</div>
      ) : lists.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-5xl mb-3"><Ico e="📋" bare /></p>
          <p className="font-medium text-gray-700">Nenhuma lista criada ainda</p>
          <p className="text-sm text-gray-400 mt-1">Crie uma lista e importe seus leads do CRM, LinkedIn ou CSV</p>
          <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowCreate(true)}>Criar primeira lista</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map(lst => {
            const src = SOURCE_BADGE[lst.source] || SOURCE_BADGE.manual;
            const isImporting = importing === lst.id;
            return (
              <Card key={lst.id} className="border border-gray-200 hover:border-blue-200 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm mb-1">{lst.name}</p>
                      <span style={{ background: src.bg, color: src.color }} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {src.icon} {src.label}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{(lst.lead_count || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">leads</p>
                    </div>
                  </div>
                  {lst.description && <p className="text-xs text-gray-400 mb-3">{lst.description}</p>}

                  {/* Import buttons */}
                  <div className="flex gap-1.5 mb-3 flex-wrap">
                    <button onClick={() => importFromCRM(lst.id)} disabled={isImporting}
                      className="flex-1 min-w-0 px-2 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-all">
                      {isImporting ? <><Ico e="⏳" /></> : <><Ico e="🏢" /></>} CRM
                    </button>
                    <button onClick={() => importFromLinkedIn(lst.id)} disabled={isImporting}
                      className="flex-1 min-w-0 px-2 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all">
                      {isImporting ? <><Ico e="⏳" /></> : <><Ico e="💼" /></>} LinkedIn
                    </button>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button onClick={() => openViewLeads(lst)}
                      className="flex-1 px-2 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 transition-all">
                      <Ico e="👁" />Ver leads
                    </button>
                    <button onClick={() => deleteList(lst.id, lst.name)}
                      className="px-2 py-1.5 text-xs text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                      <Ico e="🗑" bare />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create list modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nova Lista de Leads">
        <div className="space-y-4">
          <div>
            <Label>Nome da lista *</Label>
            <Input className="mt-1" placeholder="Ex: Prospecção Q3 2026 — Diretores SP" value={newName} onChange={e => setNewName(e.target.value)} />
          </div>
          <div>
            <Label>Descrição (opcional)</Label>
            <Input className="mt-1" placeholder="Origem, segmento ou objetivo desta lista" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={createList} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
              {creating ? 'Criando…' : 'Criar Lista'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View leads modal */}
      <Modal open={!!viewList} onClose={() => setViewList(null)} title={viewList ? `👥 ${viewList.name} — ${viewTotal} leads` : ''} maxW="max-w-4xl">
        {viewLeads.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">Nenhum lead nesta lista ainda.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Nome','Email','Telefone','Empresa','Cargo','Cidade','LinkedIn'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewLeads.map((l, i) => (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{l.name || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#2563eb' }}>{l.email || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{l.phone || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{l.company || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#6366f1' }}>{l.cargo || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{l.cidade || '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>
                      {l.linkedin_url ? <a href={l.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Ver perfil</a> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {viewTotal > 50 && (
              <p className="text-xs text-gray-400 text-center mt-3">Exibindo primeiros 50 de {viewTotal} leads</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Relatórios Tab ───────────────────────────────────────────────────────────
function RelatoriosTab({ api }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [sortByReplies, setSortByReplies] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/unique-channel/campaigns-report'); setRows(r.data || []); }
    catch { toast.error('Erro ao carregar relatório'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
  const visible = sortByReplies
    ? [...filtered].sort((a, b) => (b.reply_count || 0) - (a.reply_count || 0))
    : filtered;

  // ── helpers ──────────────────────────────────────────────────────────────────
  const STATUS_BADGE = {
    draft:     { label: 'Rascunho',  bg: '#f1f5f9', color: '#64748b' },
    active:    { label: 'Ativa',     bg: '#dcfce7', color: '#16a34a' },
    paused:    { label: 'Pausada',   bg: '#fef9c3', color: '#ca8a04' },
    completed: { label: 'Concluída', bg: '#dbeafe', color: '#2563eb' },
  };

  const Num = ({ v, pct, good }) => {
    const hasData = v > 0;
    const numColor = hasData ? (good === false ? '#ef4444' : '#f97316') : '#94a3b8';
    return (
      <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: numColor }}>{v.toLocaleString()}</div>
        {pct !== undefined && (
          <div style={{ fontSize: 10, color: hasData ? numColor : '#cbd5e1', fontWeight: 500 }}>
            {pct}%
          </div>
        )}
      </div>
    );
  };

  const exportCSV = () => {
    const headers = [
      'Campanha','Status','Leads','Emails Env.','Aberturas','Ab%','Cliques','Cl%',
      'Respostas (E-mail)','Re% (E-mail)','Devoluções','Vis.Perfil','Curtidas','Sol.Conexão','Aceitas','Ac%',
      'Resp.Conexão','Mensagens Env.','Msgs Respondidas','Msg%','InMail Env.','InMail Resp.','IM%',
      'Respostas','Taxa de Resposta (%)'
    ];
    const csv = [headers.join(','), ...visible.map(r => [
      `"${r.name}"`, r.status, r.total_enrolled,
      r.emails_sent, r.emails_opened, r.emails_opened_pct,
      r.link_clicked, r.link_clicked_pct,
      r.email_replied, r.email_replied_pct, r.email_bounced,
      r.profile_viewed, r.post_liked,
      r.connection_sent, r.connection_accepted, r.connection_accepted_pct,
      r.conn_replied, r.messages_sent, r.messages_replied, r.messages_replied_pct,
      r.inmail_sent, r.inmail_replied, r.inmail_replied_pct,
      r.reply_count ?? 0, r.reply_rate ?? 0,
    ].join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'relatorio-campanhas.csv'; a.click();
  };

  // ── column groups ─────────────────────────────────────────────────────────────
  const GRP = [
    { label: <><Ico e="📧" />E-mail</>,    cols: 5, bg: '#eff6ff', border: '#bfdbfe' },
    { label: <><Ico e="💼" />LinkedIn</>,  cols: 5, bg: '#eef2ff', border: '#c7d2fe' },
    { label: <><Ico e="💬" />Mensagens</>, cols: 4, bg: '#f0fdf4', border: '#bbf7d0' },
  ];

  const TH = ({ children, sticky = false, left = 0, width = 130, bg = '#f8fafc', grpBg }) => (
    <th style={{
      position: sticky ? 'sticky' : 'relative', left: sticky ? left : undefined,
      zIndex: sticky ? 3 : 1, background: grpBg || bg,
      padding: '8px 10px', fontSize: 10, fontWeight: 700, color: '#64748b',
      textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
      border: '1px solid #e2e8f0', minWidth: width,
    }}>
      {children}
    </th>
  );

  const TD = ({ children, sticky = false, left = 0, bg = '#fff', grpBg }) => (
    <td style={{
      position: sticky ? 'sticky' : 'relative', left: sticky ? left : undefined,
      zIndex: sticky ? 2 : 0, background: grpBg || bg,
      padding: '10px 10px', border: '1px solid #f1f5f9', verticalAlign: 'middle',
    }}>
      {children}
    </td>
  );

  return (
    <div className="space-y-4 min-w-0" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900"><Ico e="📊" />Relatório de Campanhas</h2>
          <p className="text-xs text-gray-400 mt-0.5">Métricas consolidadas de todas as campanhas · atualizado em tempo real</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50">
            {loading ? <><Ico e="⏳" /></> : <><Ico e="🔄" /></>} Atualizar
          </button>
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-all">
            ↓ Exportar CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"><Ico e="🔍" bare /></span>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Pesquisar campanha…"
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
        />
      </div>

      {/* Summary cards */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Campanhas', value: rows.length, icon: <Ico e="🚀" bare />, color: '#2563eb' },
            { label: 'Leads inscritos', value: rows.reduce((a, r) => a + r.total_enrolled, 0), icon: <Ico e="👥" bare />, color: '#7c3aed' },
            { label: 'E-mails enviados', value: rows.reduce((a, r) => a + r.emails_sent, 0), icon: <Ico e="📧" bare />, color: '#0891b2' },
            { label: 'Sol. LinkedIn', value: rows.reduce((a, r) => a + r.connection_sent, 0), icon: <Ico e="💼" bare />, color: '#4f46e5' },
            { label: 'Respostas', value: rows.reduce((a, r) => a + (r.reply_count || 0), 0), icon: <Ico e="💬" bare />, color: '#059669', clickable: true },
          ].map(c => (
            <div
              key={c.label}
              onClick={c.clickable ? () => setSortByReplies(v => !v) : undefined}
              className={`bg-white rounded-xl border px-4 py-3 flex items-center gap-3 shadow-sm transition-all ${
                c.clickable
                  ? `cursor-pointer ${sortByReplies ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-gray-100 hover:border-emerald-300'}`
                  : 'border-gray-100'
              }`}
            >
              <span className="text-2xl">{c.icon}</span>
              <div className="flex-1">
                <p className="text-xl font-bold" style={{ color: c.color }}>{c.value.toLocaleString()}</p>
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  {c.label}
                  {c.clickable && sortByReplies && <span className="text-emerald-500 font-semibold">↓ ordenado</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">
          <div className="text-4xl mb-3 animate-pulse"><Ico e="📊" bare /></div>
          <p className="text-sm">Carregando relatório…</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3"><Ico e="🔍" bare /></p>
          <p className="text-sm text-gray-400">{rows.length === 0 ? 'Nenhuma campanha criada ainda.' : 'Nenhuma campanha encontrada.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 1400, width: '100%', fontSize: 13 }}>
            <thead>
              {/* Group header row */}
              <tr>
                <th colSpan={2} style={{ position: 'sticky', left: 0, zIndex: 4, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '6px 10px' }} />
                <th colSpan={1} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textAlign: 'center' }}>LEADS</th>
                {GRP.map(g => (
                  <th key={g.label} colSpan={g.cols}
                    style={{ background: g.bg, border: `1px solid ${g.border}`, padding: '6px 10px', fontSize: 11, fontWeight: 800, color: '#374151', textAlign: 'center', letterSpacing: '0.02em' }}>
                    {g.label}
                  </th>
                ))}
              </tr>
              {/* Column header row */}
              <tr>
                <TH sticky left={0} width={200} bg="#f8fafc">Campanha</TH>
                <TH sticky left={200} width={100} bg="#f8fafc">Status</TH>
                <TH width={70}>Inscritos</TH>
                {/* Email */}
                <TH grpBg="#eff6ff" width={80}>Enviados</TH>
                <TH grpBg="#eff6ff" width={90}>Aberturas</TH>
                <TH grpBg="#eff6ff" width={80}>Cliques</TH>
                <TH grpBg={sortByReplies ? '#d1fae5' : '#eff6ff'} width={80}>
                  Respostas{sortByReplies ? ' ↓' : ''}
                </TH>
                <TH grpBg="#eff6ff" width={85}>Devoluções</TH>
                {/* LinkedIn */}
                <TH grpBg="#eef2ff" width={90}>Vis. Perfil</TH>
                <TH grpBg="#eef2ff" width={80}>Curtidas</TH>
                <TH grpBg="#eef2ff" width={95}>Sol. Conexão</TH>
                <TH grpBg="#eef2ff" width={80}>Aceitas</TH>
                <TH grpBg="#eef2ff" width={90}>Respostas</TH>
                {/* Mensagens */}
                <TH grpBg="#f0fdf4" width={90}>Mensagens</TH>
                <TH grpBg="#f0fdf4" width={90}>Respondidas</TH>
                <TH grpBg="#f0fdf4" width={80}>InMail Env.</TH>
                <TH grpBg="#f0fdf4" width={90}>InMail Resp.</TH>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const st = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
                const rowBg = i % 2 === 0 ? '#fff' : '#fafafa';
                return (
                  <tr key={r.id} style={{ transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}>
                    {/* Campanha (sticky) */}
                    <TD sticky left={0} bg={rowBg}>
                      <div style={{ maxWidth: 190 }}>
                        <p style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.name}>
                          {r.name}
                        </p>
                        {r.description && (
                          <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description}</p>
                        )}
                      </div>
                    </TD>
                    {/* Status (sticky) */}
                    <TD sticky left={200} bg={rowBg}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}>
                        {st.label}
                      </span>
                    </TD>
                    {/* Leads */}
                    <TD bg={rowBg}><Num v={r.total_enrolled} /></TD>
                    {/* Email */}
                    <TD grpBg={i % 2 === 0 ? '#eff6ff' : '#e8f0fe'}><Num v={r.emails_sent} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eff6ff' : '#e8f0fe'}><Num v={r.emails_opened} pct={r.emails_opened_pct} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eff6ff' : '#e8f0fe'}><Num v={r.link_clicked} pct={r.link_clicked_pct} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eff6ff' : '#e8f0fe'}><Num v={r.email_replied} pct={r.email_replied_pct} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eff6ff' : '#e8f0fe'}><Num v={r.email_bounced} pct={r.email_bounced_pct} good={false} /></TD>
                    {/* LinkedIn */}
                    <TD grpBg={i % 2 === 0 ? '#eef2ff' : '#e5e7ff'}><Num v={r.profile_viewed} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eef2ff' : '#e5e7ff'}><Num v={r.post_liked} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eef2ff' : '#e5e7ff'}><Num v={r.connection_sent} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eef2ff' : '#e5e7ff'}><Num v={r.connection_accepted} pct={r.connection_accepted_pct} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#eef2ff' : '#e5e7ff'}><Num v={r.conn_replied} pct={r.conn_replied_pct} /></TD>
                    {/* Mensagens */}
                    <TD grpBg={i % 2 === 0 ? '#f0fdf4' : '#e8faf0'}><Num v={r.messages_sent} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#f0fdf4' : '#e8faf0'}><Num v={r.messages_replied} pct={r.messages_replied_pct} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#f0fdf4' : '#e8faf0'}><Num v={r.inmail_sent} /></TD>
                    <TD grpBg={i % 2 === 0 ? '#f0fdf4' : '#e8faf0'}><Num v={r.inmail_replied} pct={r.inmail_replied_pct} /></TD>
                  </tr>
                );
              })}
            </tbody>

            {/* Totals row */}
            {visible.length > 1 && (
              <tfoot>
                <tr style={{ background: '#f8fafc' }}>
                  <td colSpan={2} style={{ position: 'sticky', left: 0, zIndex: 2, background: '#f1f5f9', padding: '8px 12px', fontSize: 11, fontWeight: 800, color: '#475569', border: '1px solid #e2e8f0' }}>
                    TOTAL ({visible.length} campanhas)
                  </td>
                  {[
                    'total_enrolled','emails_sent','emails_opened','link_clicked','email_replied','email_bounced',
                    'profile_viewed','post_liked','connection_sent','connection_accepted','conn_replied',
                    'messages_sent','messages_replied','inmail_sent','inmail_replied',
                  ].map(field => (
                    <td key={field} style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                      {visible.reduce((a, r) => a + (r[field] || 0), 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Email Accounts Tab ────────────────────────────────────────────────────────
function EmailAccountsTab({ api }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', from_name: '', from_email: '',
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    use_tls: true, use_ssl: false, daily_limit: 500,
  });

  const fetchAccounts = async () => {
    setLoading(true);
    try { const r = await api.get('/unique-channel/email-accounts'); setAccounts(r.data || []); }
    catch { toast.error('Erro ao carregar contas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', from_name: '', from_email: '', smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', use_tls: true, use_ssl: false, daily_limit: 500 });
    setShowForm(true);
  };

  const openEdit = (acct) => {
    setEditing(acct);
    setForm({ name: acct.name, from_name: acct.from_name, from_email: acct.from_email, smtp_host: acct.smtp_host, smtp_port: acct.smtp_port, smtp_user: acct.smtp_user, smtp_password: '', use_tls: acct.use_tls, use_ssl: acct.use_ssl, daily_limit: acct.daily_limit || 500 });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.from_email || !form.smtp_host || !form.smtp_user) {
      toast.error('Preencha todos os campos obrigatórios'); return;
    }
    if (!editing && !form.smtp_password) { toast.error('Senha SMTP obrigatória'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.smtp_password) delete payload.smtp_password;
      if (editing) await api.put(`/unique-channel/email-accounts/${editing.id}`, payload);
      else await api.post('/unique-channel/email-accounts', payload);
      toast.success(editing ? 'Conta atualizada!' : 'Conta criada!');
      setShowForm(false);
      fetchAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta conta de email?')) return;
    try { await api.delete(`/unique-channel/email-accounts/${id}`); toast.success('Conta removida'); fetchAccounts(); }
    catch { toast.error('Erro ao remover'); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Informe o e-mail de destino'); return; }
    setTestLoading(true);
    try {
      await api.post(`/unique-channel/email-accounts/${testing.id}/send-test`, {
        to_email: testEmail,
        subject: testSubject || undefined,
        body: testBody || undefined,
      });
      toast.success(`E-mail de teste enviado para ${testEmail}!`);
      setTesting(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no teste SMTP');
    } finally { setTestLoading(false); }
  };

  const PRESETS = [
    { label: 'Gmail', host: 'smtp.gmail.com', port: 587, tls: true, ssl: false },
    { label: 'Outlook / Hotmail', host: 'smtp.office365.com', port: 587, tls: true, ssl: false },
    { label: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 465, tls: false, ssl: true },
    { label: 'Zoho Mail', host: 'smtp.zoho.com', port: 587, tls: true, ssl: false },
    { label: 'Brevo (Sendinblue)', host: 'smtp-relay.brevo.com', port: 587, tls: true, ssl: false },
    { label: 'Amazon SES', host: 'email-smtp.us-east-1.amazonaws.com', port: 587, tls: true, ssl: false },
    { label: 'Mailgun', host: 'smtp.mailgun.org', port: 587, tls: true, ssl: false },
    { label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, tls: true, ssl: false },
  ];

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Contas de E-mail (SMTP)</h2>
          <p className="text-sm text-gray-500 mt-0.5">Cadastre as contas de envio que serão usadas nas campanhas de e-mail</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold shadow"
          style={{ background: `linear-gradient(135deg, ${NAVY}, ${BLUE})` }}>
          + Nova Conta
        </button>
      </div>

      {/* Accounts list */}
      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Carregando contas…</div>
      ) : accounts.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 py-16 flex flex-col items-center gap-3">
          <div className="text-4xl"><Ico e="📧" bare /></div>
          <p className="text-gray-500 font-medium">Nenhuma conta configurada</p>
          <p className="text-gray-400 text-sm">Adicione contas SMTP para usar nos envios de campanha</p>
          <button onClick={openNew} className="mt-2 px-5 py-2 rounded-xl text-white text-sm font-semibold"
            style={{ background: `linear-gradient(135deg, ${NAVY}, ${BLUE})` }}>
            Adicionar Conta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {accounts.map(acct => (
            <div key={acct.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0"
                style={{ background: `linear-gradient(135deg, ${NAVY}22, ${BLUE}22)` }}>
                <Ico e="📧" bare />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-800">{acct.name}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">{acct.from_email}</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {acct.smtp_host}:{acct.smtp_port} · {acct.use_ssl ? 'SSL' : acct.use_tls ? 'TLS' : 'Sem criptografia'}
                  {acct.daily_limit ? ` · Limite: ${acct.daily_limit}/dia` : ''}
                </p>
                <p className="text-xs text-gray-400">Usuário: {acct.smtp_user} · Nome remetente: {acct.from_name}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { setTesting(acct); setTestEmail(''); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-green-200 text-green-700 hover:bg-green-50">
                  Testar
                </button>
                <button onClick={() => openEdit(acct)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50">
                  Editar
                </button>
                <button onClick={() => handleDelete(acct.id)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-100 text-red-500 hover:bg-red-50">
                  Remover
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-lg">{editing ? 'Editar Conta SMTP' : 'Nova Conta SMTP'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-5">
              {/* Presets */}
              {!editing && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preencher automaticamente</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map(p => (
                      <button key={p.label} onClick={() => setForm(f => ({ ...f, smtp_host: p.host, smtp_port: p.port, use_tls: p.tls, use_ssl: p.ssl }))}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-blue-200 text-blue-700 hover:bg-blue-50">
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da conta <span className="text-red-400">*</span></label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ex: Email Marketing Principal"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do remetente <span className="text-red-400">*</span></label>
                  <input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))}
                    placeholder="Ex: João Silva"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-mail remetente <span className="text-red-400">*</span></label>
                  <input type="email" value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))}
                    placeholder="contato@empresa.com"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Servidor SMTP <span className="text-red-400">*</span></label>
                  <input value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))}
                    placeholder="smtp.gmail.com"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Porta SMTP</label>
                  <input type="number" value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuário SMTP <span className="text-red-400">*</span></label>
                  <input value={form.smtp_user} onChange={e => setForm(f => ({ ...f, smtp_user: e.target.value }))}
                    placeholder="seu@email.com"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha SMTP {editing ? <span className="text-gray-400 text-xs">(deixe vazio para manter)</span> : <span className="text-red-400">*</span>}
                  </label>
                  <input type="password" value={form.smtp_password} onChange={e => setForm(f => ({ ...f, smtp_password: e.target.value }))}
                    placeholder={editing ? '••••••••' : 'Senha ou App Password'}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Limite diário de envios</label>
                  <input type="number" value={form.daily_limit} onChange={e => setForm(f => ({ ...f, daily_limit: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div className="col-span-2 flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.use_tls} onChange={e => setForm(f => ({ ...f, use_tls: e.target.checked, use_ssl: e.target.checked ? false : f.use_ssl }))}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">Usar STARTTLS (porta 587)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.use_ssl} onChange={e => setForm(f => ({ ...f, use_ssl: e.target.checked, use_tls: e.target.checked ? false : f.use_tls }))}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">Usar SSL/TLS direto (porta 465)</span>
                  </label>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
                <strong><Ico e="💡" />Gmail:</strong> Ative "Acesso a app menos seguro" ou use uma <strong>App Password</strong> de 16 caracteres (Configurações → Segurança → Senhas de app).
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="px-6 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${NAVY}, ${BLUE})` }}>
                {saving ? 'Salvando…' : editing ? 'Salvar Alterações' : 'Criar Conta'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Test Send Modal */}
      {testing && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                  style={{ background: `linear-gradient(135deg, ${NAVY}22, ${BLUE}22)` }}><Ico e="📤" bare /></div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base leading-tight">Envio de Teste</h3>
                  <p className="text-xs text-gray-400">{testing.name} · {testing.from_email}</p>
                </div>
              </div>
              <button onClick={() => { setTesting(null); setTestEmail(''); setTestSubject(''); setTestBody(''); }}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Account info pill */}
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <div className="text-blue-500 text-lg"><Ico e="🖥" bare /></div>
                <div className="text-sm text-blue-700">
                  <span className="font-medium">{testing.smtp_host}</span>
                  <span className="text-blue-400">:{testing.smtp_port}</span>
                  <span className="ml-2 text-xs text-blue-400">
                    {testing.use_ssl ? 'SSL' : testing.use_tls ? 'TLS' : 'Sem criptografia'}
                  </span>
                </div>
              </div>

              {/* To email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Destinatário <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTest()}
                  placeholder="destinatario@email.com"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  autoFocus
                />
              </div>

              {/* Subject (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Assunto <span className="text-gray-400 font-normal text-xs">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={testSubject}
                  onChange={e => setTestSubject(e.target.value)}
                  placeholder="Teste de envio — UniqueChannel"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {/* Body (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mensagem <span className="text-gray-400 font-normal text-xs">(opcional)</span>
                </label>
                <textarea
                  value={testBody}
                  onChange={e => setTestBody(e.target.value)}
                  placeholder="Deixe em branco para usar a mensagem padrão de teste…"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                />
              </div>

              <p className="text-xs text-gray-400">
                O e-mail será enviado usando as credenciais SMTP salvas desta conta.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setTesting(null); setTestEmail(''); setTestSubject(''); setTestBody(''); }}
                className="px-5 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={handleTest}
                disabled={testLoading || !testEmail}
                className="flex items-center gap-2 px-6 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
                style={{ background: `linear-gradient(135deg, #16a34a, #15803d)` }}>
                {testLoading ? (
                  <><span className="animate-spin"><Ico e="⏳" bare /></span> Enviando…</>
                ) : (
                  <><span><Ico e="📤" bare /></span> Enviar Teste</>
                )}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

// ─── Main UniqueChannel component ─────────────────────────────────────────────
export default function UniqueChannel({ api }) {
  const [activeTab, setActiveTab] = useState('scoring');
  const [openCampaign, setOpenCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState({});

  const fetchTemplates = async () => {
    try { const r = await api.get('/unique-channel/templates'); setTemplates(r.data || []); }
    catch {}
  };
  const fetchIntegrationStatus = async () => {
    try { const r = await api.get('/integrations/status'); setIntegrationStatus(r.data || {}); }
    catch {}
  };
  useEffect(() => { fetchTemplates(); fetchIntegrationStatus(); }, []);

  const TABS = [
    { id: 'scoring',      label: <><Ico e="🎯" className="transition-colors group-hover:text-blue-500" />Lead Scoring</> },
    { id: 'campaigns',    label: <><Ico e="🚀" className="transition-colors group-hover:text-pink-500" />Campanhas</> },
    { id: 'listas',       label: <><Ico e="📋" className="transition-colors group-hover:text-teal-500" />Listas de Leads</> },
    { id: 'relatorios',   label: <><Ico e="📊" className="transition-colors group-hover:text-green-500" />Relatórios</> },
    { id: 'templates',    label: <><Ico e="📝" className="transition-colors group-hover:text-amber-500" />Templates</> },
    { id: 'contas_email', label: <><Ico e="📧" className="transition-colors group-hover:text-orange-500" />Contas de Email</> },
    { id: 'criacao_ia',   label: <><Ico e="🎨" className="transition-colors group-hover:text-purple-500" />Criação IA</> },
  ];

  if (openCampaign) {
    return (
      <div className="space-y-0">
        {/* Top nav bar */}
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, ${BLUE})` }} className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0">U</div>
          <span className="text-gray-400 text-sm">UniqueChannel</span>
          <span className="text-gray-300">›</span>
          <span className="text-gray-700 text-sm font-medium">{openCampaign.name}</span>
        </div>
        <CampaignDetail
          campaign={openCampaign}
          api={api}
          templates={templates}
          sources={openCampaign.sources || {}}
          onBack={() => setOpenCampaign(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl px-7 py-5 flex items-center gap-4" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${BLUE} 70%, ${TEAL} 100%)` }}>
        <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center text-3xl"><Ico e="🎯" bare /></div>
        <div>
          <h1 className="text-2xl font-bold text-white">UniqueChannel</h1>
          <p className="text-white/70 text-sm mt-0.5">Lead Scoring · Campanhas omnichannel · Email · LinkedIn · WhatsApp</p>
        </div>
        <div className="ml-auto text-right hidden sm:block">
          <p className="text-white/50 text-xs">Motor de Growth</p>
          <p className="text-white font-semibold text-sm">Qualifique e converta</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full overflow-x-auto nx-scrollbar">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-max">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`group px-4 sm:px-6 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'scoring' && (
  <div style={{ isolation: 'auto' }}>
    <LeadScoring api={api} embedded />
  </div>
)}
      {activeTab === 'campaigns' && (
        <CampaignsDashboard
          api={api}
          templates={templates}
          integrationStatus={integrationStatus}
          onOpenCampaign={(c) => { setOpenCampaign(c); fetchTemplates(); }}
        />
      )}
      {activeTab === 'listas' && (
        <LeadListsTab api={api} />
      )}
      {activeTab === 'relatorios' && (
        <RelatoriosTab api={api} />
      )}
      {activeTab === 'templates' && (
        <TemplatesTab api={api} />
      )}
      {activeTab === 'contas_email' && (
        <EmailAccountsTab api={api} />
      )}
      {activeTab === 'criacao_ia' && (
        <CriacaoIATab api={api} onSavedToTemplates={() => { fetchTemplates(); setActiveTab('templates'); }} />
      )}
    </div>
  );
}
